import { useState } from 'react'
import { Account } from '../../../../../shared/types'
import { EditableCell } from '../../../shared/components'

interface AccountRowProps {
  account: Account
  liquidValue: number
  semiLiquidValue: number
  illiquidValue: number
  onUpdateValues: (accountId: number, values: {
    liquidValue: number
    semiLiquidValue: number
    illiquidValue: number
  }) => void
  onEditAccount: (account: Account) => void
  onDeleteAccount: (accountId: number) => void
  isUpdating?: boolean
}

export default function AccountRow({
  account,
  liquidValue,
  semiLiquidValue,
  illiquidValue,
  onUpdateValues,
  onEditAccount,
  onDeleteAccount,
  isUpdating = false
}: AccountRowProps) {
  const [editingCell, setEditingCell] = useState<string | null>(null)

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const parseCurrency = (value: string) => {
    // Remove currency symbols and commas, then parse
    const cleaned = value.replace(/[$,]/g, '')
    return parseFloat(cleaned) || 0
  }

  const handleValueUpdate = (field: 'liquid' | 'semiLiquid' | 'illiquid', newValue: number) => {
    const values = {
      liquidValue: field === 'liquid' ? newValue : liquidValue,
      semiLiquidValue: field === 'semiLiquid' ? newValue : semiLiquidValue,
      illiquidValue: field === 'illiquid' ? newValue : illiquidValue
    }
    
    onUpdateValues(account.id, values)
    setEditingCell(null)
  }

  const totalValue = liquidValue + semiLiquidValue + illiquidValue

  return (
    <tr style={{
      backgroundColor: isUpdating ? 'rgba(0, 176, 255, 0.05)' : 'transparent',
      opacity: isUpdating ? 0.7 : 1,
      transition: 'all 0.2s ease'
    }}>
      <td style={{
        padding: '0.75rem',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-light)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{account.name}</span>
          <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
            <button
              onClick={() => onEditAccount(account)}
              disabled={isUpdating}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary-blue)',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                padding: '0.25rem',
                fontSize: '0.875rem'
              }}
              title="Edit account"
            >
              ✏️
            </button>
            <button
              onClick={() => onDeleteAccount(account.id)}
              disabled={isUpdating}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-red)',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                padding: '0.25rem',
                fontSize: '0.875rem',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isUpdating) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 82, 82, 0.1)'
                  e.currentTarget.style.borderRadius = '4px'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
              title="Delete account"
            >
              🗑️
            </button>
          </div>
        </div>
      </td>
      <td style={{
        padding: '0.75rem',
        borderBottom: '1px solid var(--border-color)',
        textAlign: 'right'
      }}>
        <EditableCell
          value={liquidValue}
          onSave={(value) => handleValueUpdate('liquid', value)}
          formatValue={formatCurrency}
          parseValue={parseCurrency}
          disabled={isUpdating}
          isEditing={editingCell === 'liquid'}
          onEditingChange={(editing) => setEditingCell(editing ? 'liquid' : null)}
        />
      </td>
      <td style={{
        padding: '0.75rem',
        borderBottom: '1px solid var(--border-color)',
        textAlign: 'right'
      }}>
        <EditableCell
          value={semiLiquidValue}
          onSave={(value) => handleValueUpdate('semiLiquid', value)}
          formatValue={formatCurrency}
          parseValue={parseCurrency}
          disabled={isUpdating}
          isEditing={editingCell === 'semiLiquid'}
          onEditingChange={(editing) => setEditingCell(editing ? 'semiLiquid' : null)}
        />
      </td>
      <td style={{
        padding: '0.75rem',
        borderBottom: '1px solid var(--border-color)',
        textAlign: 'right'
      }}>
        <EditableCell
          value={illiquidValue}
          onSave={(value) => handleValueUpdate('illiquid', value)}
          formatValue={formatCurrency}
          parseValue={parseCurrency}
          disabled={isUpdating}
          isEditing={editingCell === 'illiquid'}
          onEditingChange={(editing) => setEditingCell(editing ? 'illiquid' : null)}
        />
      </td>
      <td style={{
        padding: '0.75rem',
        borderBottom: '1px solid var(--border-color)',
        textAlign: 'right',
        fontFamily: 'var(--font-mono)',
        fontWeight: '600',
        color: account.type === 'asset' 
          ? (totalValue >= 0 ? 'var(--accent-green)' : 'var(--accent-red)')
          : (totalValue >= 0 ? 'var(--accent-red)' : 'var(--accent-green)')
      }}>
        {formatCurrency(totalValue)}
      </td>
    </tr>
  )
}